import React, { useState } from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Template products — faithful rewrite of legacy molecules/TemplateProducts +
// Product (Strapi sections.template-new-product-section). A cream band with a
// centered header and a carousel of product cards (icon, title, description,
// Learn More); each card fills blue on hover. 3-up carousel via client useState
// (legacy used react-slick). No react-slick / next/image.
export const templateProductsSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  cards: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().optional(),
        url: safeUrl.optional(),
        iconUrl: z.string().optional(),
      }),
    )
    .min(1),
});

type TemplateProductsProps = z.infer<typeof templateProductsSchema>;

const Band = styled.section`
  background: #fcfaf5;
  padding: 4.8rem 0;
  overflow: hidden;

  @media (min-width: 62em) {
    padding: 12rem 0;
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: 1.2rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-align: center;

  @media (min-width: 62em) {
    font-size: 1.5rem;
    letter-spacing: 4px;
  }
`;

const Heading = styled.h2`
  margin: 2.2rem 1.6rem 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.4rem;
  text-align: center;

  @media (min-width: 62em) {
    margin: 2.2rem auto 0;
    font-size: 4.8rem;
    letter-spacing: -2px;
  }
`;

const Carousel = styled.div`
  max-width: 112rem;
  margin: 4.8rem auto 0;
  padding: 0 1.6rem;

  @media (min-width: 62em) {
    margin-top: 6.4rem;
  }
`;

const Viewport = styled.div`
  overflow: hidden;
`;

const Track = styled.div`
  display: flex;
  gap: 1.9rem;
  transition: transform 0.5s ease;
`;

const Card = styled.a`
  display: flex;
  flex: 0 0 30rem;
  flex-direction: column;
  gap: 1.6rem;
  padding: 2.4rem;
  border-radius: 1.6rem;
  background: #eaf0ff;
  text-decoration: none;
  transition: background 0.4s ease, color 0.4s ease;

  @media (min-width: 62em) {
    flex-basis: 36rem;
  }

  &:hover {
    background: #0a3aa8;
  }
  &:hover * {
    color: #ffffff;
  }
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 8rem;
  height: 8rem;
  border-radius: 50%;
  background: #ffffff;
`;

const Icon = styled.img`
  width: 5.2rem;
  height: 5.2rem;
  object-fit: contain;
`;

const CardTitle = styled.h4`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 2.4rem;
`;

const CardDesc = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.8rem;
`;

const CardLink = styled.span`
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 550;
  font-size: 1.6rem;
  text-decoration: underline;
  text-underline-offset: 5px;
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.6rem;
  margin-top: 2.4rem;
`;

const Arrow = styled.button`
  appearance: none;
  width: 4.8rem;
  height: 4.8rem;
  border: 1px solid #0a3aa8;
  border-radius: 50%;
  background: #ffffff;
  color: #0a3aa8;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

function TemplateProducts({ eyebrow, heading, cards }: TemplateProductsProps) {
  const [active, setActive] = useState(0);
  const last = cards.length - 1;
  const go = (i: number) => setActive(Math.max(0, Math.min(last, i)));

  return (
    <Band>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <Heading>{heading}</Heading>
      <Carousel>
        <Viewport>
          <Track style={{ transform: `translateX(calc(-${active} * (36rem + 1.9rem)))` }}>
            {cards.map((c, i) => (
              <Card key={`${c.title}-${i}`} href={c.url ?? '#'}>
                {c.iconUrl ? (
                  <IconCircle>
                    <Icon src={c.iconUrl} alt="" />
                  </IconCircle>
                ) : null}
                <CardTitle>{c.title}</CardTitle>
                {c.description ? <CardDesc>{c.description}</CardDesc> : null}
                {c.url ? <CardLink>LEARN MORE →</CardLink> : null}
              </Card>
            ))}
          </Track>
        </Viewport>
        {cards.length > 3 ? (
          <Controls>
            <Arrow onClick={() => go(active - 1)} disabled={active === 0} aria-label="Previous">
              &#8249;
            </Arrow>
            <Arrow onClick={() => go(active + 1)} disabled={active >= last - 2} aria-label="Next">
              &#8250;
            </Arrow>
          </Controls>
        ) : null}
      </Carousel>
    </Band>
  );
}

export const templateProductsBlock: BlockDefinition<TemplateProductsProps> = {
  type: 'templateProducts',
  schema: templateProductsSchema,
  Renderer: TemplateProducts,
};
